"use client";

import React, { useState, useMemo } from 'react';
import { BlogPost } from '../../lib/blog';
import { Search, X } from 'lucide-react';
import BlogCard from './BlogCard';

interface BlogSearchProps {
    posts: BlogPost[];
    onSearch?: (filteredPosts: BlogPost[], searchTerm: string) => void;
}

export default function BlogSearch({ posts, onSearch }: BlogSearchProps) {
    const [searchTerm, setSearchTerm] = useState('');

    // Filter posts based on search
    const filteredPosts = useMemo(() => {
        let filtered = posts;

        // Filter by search
        if (searchTerm) {
            filtered = filtered.filter(post =>
                post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (post.excerpt && post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }

        return filtered;
    }, [posts, searchTerm]);

    React.useEffect(() => {
        if (onSearch) {
            onSearch(filteredPosts, searchTerm);
        }
    }, [filteredPosts, searchTerm, onSearch]);

    return (
        <div className="w-full">
            {/* Enhanced Search bar */}
            <div className="relative group">
                <div className="relative bg-white rounded-2xl shadow-sm border border-gray-200 hover:border-[#6547D9] transition-colors duration-300">
                    <div className="flex items-center">
                        <div className="pl-5 pr-3 py-4">
                            <Search className={`w-5 h-5 transition-colors duration-300 ${
                                searchTerm ? 'text-[#6547D9]' : 'text-gray-400'
                            }`} />
                        </div>
                        <input
                            type="text"
                            placeholder="Search articles, topics, or keywords..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="flex-1 py-4 pr-3 text-gray-900 placeholder-gray-400 bg-transparent focus:outline-none text-base"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="mr-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                        <div className="pr-5">
                            <button className="px-5 py-2 bg-[#6547D9] text-white font-medium rounded-xl hover:bg-[#5638C7] transition-colors duration-300">
                                Search
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Enhanced Search results info */}
            {searchTerm && (
                <div className="mt-8 text-center">
                    <div className="inline-flex items-center gap-3 px-5 py-3 bg-gray-50 rounded-full border border-gray-200">
                        <span className="text-sm font-medium text-gray-700">
                            {filteredPosts.length === 0 ? (
                                <span className="text-red-600">No articles found</span>
                            ) : (
                                <>
                                    <span className="text-[#6547D9] font-bold">{filteredPosts.length}</span>
                                    {' article'}{filteredPosts.length !== 1 ? 's' : ''} found
                                </>
                            )}
                        </span>
                        <span className="text-gray-400">|</span>
                        <span className="text-sm text-gray-600">
                            Searching for: <span className="font-medium text-gray-900">"{searchTerm}"</span>
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}
